/**
 * AppealBondPanel — generate the appeal bond for a case (Lloyd, Aug 19 2026: *"Yes, build away"*).
 *
 * Three decisions worth knowing about, because each of them is about a document that gets sworn to
 * in front of a notary and filed with a court:
 *
 *  1. **Blockers are shown before anything is generated**, not after a failure. The readiness call
 *     produces nothing; it just reports what is missing, in words staff can act on.
 *  2. **The two sums are shown back after generating.** They are the values Lloyd swears to, and an
 *     amount that is wrong is far likelier to be caught here than inside a PDF opened on a phone.
 *  3. **The signature and notary dates are optional and stay hidden** unless asked for. They default
 *     to the judgment date, which Lloyd said is right "almost always" — the one case he has seen
 *     where they differed is a jury-trial appeal, and that is what the override is for.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Divider,
  Link as MuiLink,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import BondApi, {
  AppealBondBlockedError,
  AppealBondReadinessDto,
  AppealBondResultDto,
} from '../../api/BondApi';

interface AppealBondPanelProps {
  Ticket_guid: string;
}

/** A base64 PDF the browser can open without the page having to host it anywhere. */
function pdfObjectUrl(base64: string): string {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
}

const AppealBondPanel: React.FC<AppealBondPanelProps> = ({ Ticket_guid }) => {
  const [readiness, setReadiness] = useState<AppealBondReadinessDto | null>(null);
  const [judgmentDate, setJudgmentDate] = useState('');
  const [showDateOverrides, setShowDateOverrides] = useState(false);
  const [signatureDate, setSignatureDate] = useState('');
  const [notaryDate, setNotaryDate] = useState('');
  const [fineInput, setFineInput] = useState('');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState<AppealBondResultDto | null>(null);
  const [blockers, setBlockers] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  const loadReadiness = useCallback(async () => {
    try {
      const dto = await BondApi.readiness(Ticket_guid, judgmentDate || undefined);
      setReadiness(dto);
      setBlockers(dto.blockers);
      if (dto.courtFineCents !== null && fineInput === '') {
        setFineInput((dto.courtFineCents / 100).toFixed(2));
      }
    } catch {
      // Non-fatal. The generate call re-checks server-side and returns the same reasons, so a
      // failed readiness probe must not stop someone who knows the case is ready.
    }
    // fineInput is intentionally not a dependency: re-running on every keystroke would fight the
    // user for control of the field.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Ticket_guid, judgmentDate]);

  useEffect(() => {
    loadReadiness();
  }, [loadReadiness]);

  // Revoke the previous object URL when it is replaced or the panel unmounts, or every regenerate
  // leaks a blob for the life of the page.
  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    setBlockers([]);
    setResult(null);
    try {
      const trimmedFine = fineInput.trim();
      const dto = await BondApi.generate(Ticket_guid, {
        judgmentDate,
        signatureDate: signatureDate || null,
        notaryDate: notaryDate || null,
        fineCents: trimmedFine === '' ? null : Math.round(Number(trimmedFine) * 100),
      });
      setResult(dto);
      setPdfUrl(pdfObjectUrl(dto.pdfBase64));
      await loadReadiness();
    } catch (err) {
      if (err instanceof AppealBondBlockedError) {
        setBlockers(err.blockers.length > 0 ? err.blockers : [err.message]);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to generate the bond');
      }
    } finally {
      setGenerating(false);
    }
  };

  const fineDollars = Number(fineInput);
  const canSubmit =
    judgmentDate !== '' && Number.isFinite(fineDollars) && fineDollars > 0 && !generating;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
        Appeal bond
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Generates the bond for this case
        {readiness ? (readiness.courtType === 'jp' ? ' (Justice Court form)' : ' (Municipal form)') : ''} and
        emails it to Lloyd to print, sign and notarise.
      </Typography>

      {blockers.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>This case is not ready for a bond</AlertTitle>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {blockers.map((b) => (
              <li key={b}>{b}</li>
            ))}
          </ul>
        </Alert>
      )}

      {/*
        Dani, 26 Oct 2026: "We're having to redo appeal bonds because they're generating with the
        wrong information." The court record is checked here, before the bond is made from it.
      */}
      {readiness?.courtWarnings && readiness.courtWarnings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Check the court before generating</AlertTitle>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {readiness.courtWarnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
          {readiness.courtGuid && (
            <a href={`/court?Court_guid=${readiness.courtGuid}`} target="_blank" rel="noopener noreferrer">
              Open the court
            </a>
          )}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 1 }}>
        <TextField
          label="Court fine (USD)"
          type="number"
          size="small"
          value={fineInput}
          onChange={(e) => setFineInput(e.target.value)}
          inputProps={{ min: 0.01, step: 0.01 }}
          helperText="The bond swears to twice this"
          sx={{ maxWidth: 220 }}
        />
        <TextField
          label="Judgment date"
          type="date"
          size="small"
          value={judgmentDate}
          onChange={(e) => setJudgmentDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          helperText="The judgment being appealed"
          sx={{ maxWidth: 220 }}
        />
      </Stack>

      {/*
        Hidden by default. Lloyd: the signature and notary dates are "almost always the same day" as
        the judgment; the one case in his practice where they differed was a jury-trial appeal.
        Showing three date fields by default would invite three answers where one is correct.
      */}
      {showDateOverrides ? (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 1 }}>
          <TextField
            label="Signature date"
            type="date"
            size="small"
            value={signatureDate}
            onChange={(e) => setSignatureDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ maxWidth: 220 }}
          />
          <TextField
            label="Notary date"
            type="date"
            size="small"
            value={notaryDate}
            onChange={(e) => setNotaryDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ maxWidth: 220 }}
          />
        </Stack>
      ) : (
        <Button size="small" onClick={() => setShowDateOverrides(true)} sx={{ mb: 1 }}>
          The bond was signed on a different day
        </Button>
      )}

      <Box>
        <Button
          variant="contained"
          onClick={handleGenerate}
          disabled={!canSubmit}
          startIcon={generating ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {generating ? 'Generating…' : 'Generate and send to Lloyd'}
        </Button>
      </Box>

      {result && (
        <Alert severity={result.deliveryError ? 'warning' : 'success'} sx={{ mt: 2 }}>
          <AlertTitle>
            {result.deliveryError ? 'Bond generated, but not emailed' : `Sent to ${result.sentTo}`}
          </AlertTitle>
          {/*
            The sums are repeated here on purpose: these are the figures Lloyd swears to in front of
            a notary, and this is the last point at which a wrong one is cheap to catch.
          */}
          <Box component="div" sx={{ mb: 1 }}>
            <div>
              Judgment (¶1): <strong>{result.judgmentAmount}</strong>
            </div>
            <div>
              Surety sum (¶2): <strong>{result.suretyAmount}</strong>
            </div>
          </Box>
          {result.deliveryError && <div style={{ marginBottom: 8 }}>{result.deliveryError}</div>}
          {pdfUrl && (
            <MuiLink href={pdfUrl} target="_blank" rel="noopener noreferrer">
              Open {result.fileName}
            </MuiLink>
          )}
        </Alert>
      )}

      <Divider sx={{ my: 3 }} />
    </Box>
  );
};

export default AppealBondPanel;
